import * as React from 'react';
import { compose, setDisplayName, withProps, withHandlers } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { physics } from 'popmotion';
import scroll from 'stylefire/scroll';
import { FadeCollapse } from 'ui/transitions';
import { Report } from 'types';

import './SectionLayout.css';

type HasReport = {
    report: Report;
};
type SectionDescription = {
    key: string;
    component: React.ReactNode;
    visible?: (props: HasReport) => boolean;
    height?: (props: HasReport) => number | string;
};

type OwnProps = {
    report: Report;
    children: SectionDescription[];
};

export const scrollTo = (id: string) => {
    let element = document.querySelector(id) as HTMLElement;
    if (!element) {
        throw new Error(`Failed to find element with id ${id}`);
    }
    const parent = document.getElementById('section-scroller');
    if (!parent) {
        throw new Error('failed to find section-scroller element');
    }

    // Add up the nested offsets to be able to scroll to deeper elements
    // Note: for this to work, all elements in the path from the deep
    // element to #section-scroller must be at least position: 'relative'
    let offsetTop = 0;
    do {
        offsetTop += element.offsetTop;
        element = element.parentElement!;
    }
    while (element && element.parentElement !== parent);

    const parentScroller = scroll(parent);
    physics({
        from: parentScroller.get('top'),
        to: offsetTop,
        springStrength: 400,
        friction: .97,
    }).start((v: number) => parentScroller.set('top', Math.round(v)));
};

const safeId = (id: string) => id.replace(/\./g, '_');

const RawSectionLayout = ({ handleClickLink, children, styles, t, ...props }: OwnProps & RecomposeProps & OemProps & TranslateProps) =>
    <div
        id="section-scroller"
        className="abs section-layout"
        style={ styles.container }
    >
        <nav>
            { children.map(({ key, visible }: SectionDescription) =>
                <FadeCollapse
                    key={ key }
                    visible={ visible ? visible(props) : true }
                    duration={ 400 }
                    height="2em"
                >
                    <div
                        id={ `designer-page-section-${safeId(key)}-link` }
                        className="designer-page-section-link"
                        onClick={ handleClickLink }
                        style={ styles.link }
                        tabIndex={ 0 }
                    >
                        { t(key) }
                    </div>
                </FadeCollapse>
            ) }
        </nav>
        <div>
            <div style={ styles.spacer }>
                &nbsp;
            </div>
            { children.map(({ key, component, visible, height }: SectionDescription) =>
                (visible && height)
                    ? <FadeCollapse
                        key={ key }
                        visible={ visible(props) }
                        duration={ 400 }
                        height={ height(props) }
                        className="rel"
                        unmountOnExit={ true }
                    >
                        <section
                            id={ `designer-page-section-${safeId(key)}` }
                        >
                            <h2>
                                { t(key) }
                            </h2>
                            { component }
                        </section>
                    </FadeCollapse>
                    : (visible ? visible(props) : true) && <section
                        key={ key }
                        id={ `designer-page-section-${safeId(key)}` }
                        className="rel"
                    >
                        <h2>
                            { t(key) }
                        </h2>
                        { component }
                    </section>
            ) }
            <div style={ styles.doubleSpacer }>
                &nbsp;
            </div>
        </div>
    </div>
;

type RecomposeProps = {
    handleClickLink: React.MouseEventHandler<HTMLDivElement>;
};

export const SectionLayout = compose<OwnProps & RecomposeProps & OemProps & TranslateProps, OwnProps>(
    setDisplayName('SectionLayout'),
    skin,
    translate(),
    withProps(({ styles }: OemProps) => ({
        styles: {
            container: {
                borderBottom: styles.Bordered.border,
                borderTop: styles.Bordered.border,
            },
            link: {
                color: styles.Window.color,
            },
            spacer: {
                height: '1em',
            },
            doubleSpacer: {
                height: '2em',
            },
        },
    })),
    withHandlers({
        handleClickLink: () => (e: React.MouseEvent<HTMLDivElement>) => {
            const linkId = e.currentTarget.id;
            const sectionId = linkId.replace(/-link$/, '');
            scrollTo(`#${sectionId}`);
        },
    }),
)(RawSectionLayout);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/SectionLayout.tsx